import { CreateMessageRequest, EventTargetType, GetMessageResponse, MessageChannel, MessageStatus } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { deleteOrganizationMessage, getMessageHtml, messagePlainTextDescription } from '../helpers/api/messages-api';

import challengeData from '../helpers/state/challenge-data';
import { isDefined } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('past-scheduled-message-component')
export class PastScheduledMessageComponent extends LitElement {

    @property({ type: Array })
    data: GetMessageResponse[] = [];

    @property()
    private readonly message_target_type: EventTargetType = EventTargetType.ActivatedChallenge;

    @state()
    sortBy: string = '';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    @state()
    messages: GetMessageResponse[] = [];

    @state()
    previewMode: boolean = false;

    @state()
    previewMessage?: GetMessageResponse | CreateMessageRequest;

    @state()
    selectedTab?: number = undefined;

    amplitude_callsite: string = 'Challenge Participants Progress Modal';

    options = ['Sent', 'Scheduled', 'Automated'];

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                // Sort using string comparison
                this.messages = this.data.sort((a, b) => {
                    // Get date string for message a
                    let aDateStr: string = '';
                    if (isDefined(a.sent_at)) {
                        aDateStr = a.sent_at;
                    } else if (a.run_at === 'Immediate') {
                        aDateStr = isDefined(a.created_at) ? a.created_at : '';
                    } else if (typeof a.run_at === 'object' && 'Scheduled' in a.run_at) {
                        aDateStr = isDefined(a.run_at.Scheduled) ? a.run_at.Scheduled : '';
                    }

                    // Get date string for message b
                    let bDateStr: string = '';
                    if (isDefined(b.sent_at)) {
                        bDateStr = b.sent_at;
                    } else if (b.run_at === 'Immediate') {
                        bDateStr = isDefined(b.created_at) ? b.created_at : '';
                    } else if (typeof b.run_at === 'object' && 'Scheduled' in b.run_at) {
                        bDateStr = isDefined(b.run_at.Scheduled) ? b.run_at.Scheduled : '';
                    }

                    // Compare strings in reverse order for newest first
                    return bDateStr.localeCompare(aDateStr);
                });
            }
        });
    }

    private changeTab(e: CustomEvent | any) {
        this.selectedTab = e.detail.tabIndex;

        this.changeMessageType(this.options[this.selectedTab ?? 0]);
    }

    private changeMessageType(RunType: string) {
        if (RunType === 'Sent') {
            this.messages = this.data.filter((message) => message.status === MessageStatus.Sent || message.status === MessageStatus.Pending);
        } else if (RunType === 'Automated') {
            this.messages = this.data.filter((message) => message.status === MessageStatus.AutomationActive || message.status === MessageStatus.AutomationDisabled);
        } else if (RunType === 'Scheduled') {
            this.messages = this.data.filter((message) => message.status === MessageStatus.Scheduled || message.status === MessageStatus.Failed);
        } else {
            this.messages = this.data;
        }
    }

    private subjectLine(message: GetMessageResponse): string {
        const payload = message.payload;

        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.subject;
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.subject;
        }

        return 'No Subject';
    }

    private body(message: GetMessageResponse): string {
        const payload = message.payload;

        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.body;
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.body;
        }

        return 'No Body';
    }

    private includeSecretLink(message: GetMessageResponse): boolean {
        const payload = message.payload;

        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.include_unique_link ?? false;
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.include_unique_link ?? false;
        }

        return false;
    }

    private formattedDate(dateString: string): string {
        const formattedDate = dateString ?
        new Date(dateString).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
                    }) : '';
        return formattedDate;
    }

    private sortClickHandler(sortBy: string) {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }

        this.messages = this.messages.sort((a, b) => {
            const comparison = 0;
            if (sortBy === 'subject') {
                const subjectA = this.subjectLine(a).toLowerCase();
                const subjectB = this.subjectLine(b).toLowerCase();
                return subjectA.localeCompare(subjectB);
            } else if (sortBy === 'status') {
                return a.status.localeCompare(b.status);
            } else if (sortBy === 'sent') {
                const dateA = isDefined(a.sent_at) ? new Date(a.sent_at) : new Date(a.created_at ?? '');
                const dateB = isDefined(b.sent_at) ? new Date(b.sent_at) : new Date(b.created_at ?? '');
                return dateA.getTime() - dateB.getTime();
            }

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    private async openPreview(message: GetMessageResponse | CreateMessageRequest) {
        this.previewMode = true;
        this.previewMessage = message;
        await this.updateComplete;
    }

    private async setEditableMessage(message: GetMessageResponse) {
        this.dispatchEvent(new CustomEvent('set-edited-message', {
            bubbles: true,
            composed: true,
            detail: { message }
        }));
    }

    private async stopMessage(message: GetMessageResponse) {
        if (!confirm(`Are you sure you want to ${message.status === MessageStatus.AutomationActive ? 'stop' : 'delete'} this message?`)) {
            return;
        }
        // automated messages will just be stopped, not deleted
        await deleteOrganizationMessage(this.amplitude_callsite, topicData.organizationId ?? '', message._id.$oid, topicData.topicKey);
    }


    render() {
        if (this.previewMode  && isDefined(this.previewMessage)) {
            const subject = this.subjectLine(this.previewMessage as GetMessageResponse);
            const body = this.body(this.previewMessage as GetMessageResponse);
            const includeUniqueLink = this.includeSecretLink(this.previewMessage as GetMessageResponse);

            if (this.previewMessage.channel === MessageChannel.SMS) {
                // For SMS, show simple text preview with challenge name prefix
                const challengeName = challengeData.challenge?.name ?? 'Challenge';

                return html`
                    <div class="preview-container">
                        <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center;">
                            <div class="preview-subject">
                                <h3>SMS Preview</h3>
                            </div>
                            <form-button
                                variant="outline"
                                size="sm"
                                class="close-button"
                                @click="${() => { this.previewMode = false; }}">
                                Close Preview
                            </form-button>
                        </div>
                        <div class="sms-preview-content" style="background: #f5f5f5; padding: 16px; border-radius: 8px; margin-top: 16px; font-family: monospace; border: 1px solid #ddd;">
                            <div style="font-weight: bold; margin-bottom: 8px;">${challengeName}:</div>
                            <div>${body}</div><br />
                            <div style="margin-bottom: 12px;">Reply <strong>STOP</strong> to opt out</div>
                            ${includeUniqueLink ? html`
                            <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 12px; color: #666;">
                                *User's participation link will be appended here*
                            </div>
                            ` : ''}
                        </div>
                        <br />
                        <strong>Legal Notice:</strong> By sending SMS messages, you confirm that recipients have explicitly opted in to receive text messages from your organization. "Reply STOP to opt out" will be automatically added to messages as required by regulations.
                    </div>
                `;
            }

            return html`
                <div class="preview-container">
                    <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center;">
                        <div class="preview-subject">
                            <h3>Subject: ${subject}</h3>
                        </div>
                        <form-button
                            variant="outline"
                            size="sm"
                            class="close-button"
                            @click="${() => {this.previewMode = false;}}">
                            Close Preview
                        </form-button>
                    </div>
                    <div class="email-preview-content">
                        ${this.message_target_type === EventTargetType.ActivatedChallenge ?
                            unsafeHTML(getMessageHtml(this.message_target_type, this.previewMessage?.payload, challengeData.challenge?.registration_settings?.logo_url ?? '', challengeData.challenge?.registration_settings?.cover_image_url ?? ''))
                        : this.message_target_type === EventTargetType.Form
                            ? unsafeHTML(getMessageHtml(this.message_target_type, this.previewMessage?.payload, topicData.topic?.header_url ?? '', topicData.topic?.custom_social_media_image ?? ''))
                            : ''
                        }
                    </div>
                </div>
            `;        }
        return html`
                <tabs-component id="tabsComponent"
                    .options=${this.options}
                    .type=${'pill'}
                    style="width: 100%;margin-bottom: 10px; margin-top: 10px;"
                    .tabIndex=${this.selectedTab}
                    @on-select-tab=${(e: CustomEvent) => {
                        this.changeTab(e);
                    }}
                ></tabs-component>
                <div class="challenges-wrapper">
                    <div class="header-row">
                        <div class="header-cell channel" >Channel</div>
                        <div class="header-cell text" @click="${() => this.sortClickHandler('subject')}">Subject Line ${this.sortBy === 'subject' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell text" @click="${() => this.sortClickHandler('sent')}">Status ${this.sortBy === 'sent' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number" >Sends</div>
                        <div class="header-cell number">Actions</div>
                    </div>
                    ${this.messages.map((message) => html`
                        <div class="challenge-row">
                            <div class="data-cell channel">
                                ${message.channel.toUpperCase()}
                            </div>
                            <div class="data-cell text">
                                ${this.subjectLine(message)}
                            </div>
                            <div class="data-cell text">${messagePlainTextDescription(message)}</div>
                            <div class="data-cell number">
                                Successful: ${message.successful_sends ?? 0} <br> Failed: ${message.failed_sends?.length ?? 0}</div>
                            <div class="data-cell number" style="display: flex;flex-wrap: wrap;justify-content: flex-start;gap: 2px;">
                                ${message.status === MessageStatus.Scheduled || message.status === MessageStatus.AutomationActive || message.status === MessageStatus.AutomationDisabled ? html`
                                    <form-button variant="outline" size="xs" .icon=${'edit'} class="aqua-blue" @click="${async () => await this.setEditableMessage(message)}">Edit</form-button>
                                    ` : ''}
                                <form-button variant="outline" size="xs" .icon=${'visibility'} class="aqua-blue" @click="${async () => await this.openPreview(message)}">View</form-button>
                                ${message.status === MessageStatus.AutomationActive ? html`
                                    <form-button variant="outline" size="xs" .icon=${'pause'} class="aqua-blue" @click="${async () => await this.stopMessage(message)}">Delete</form-button>
                                ` : ''}
                                ${message.status === MessageStatus.Scheduled || message.status === MessageStatus.Failed ? html`
                                    <form-button variant="outline" size="xs" .icon=${'delete'} class="aqua-blue" @click="${async () => await this.stopMessage(message)}">Delete</form-button>
                                ` : ''}
                            </div>
                        </div>
                    `)}
                </div>
            </div>
        `;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
                font-size: 12px;
                display: flex;
                flex-direction: column;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }



            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.channel {
                text-align: center;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
                cursor: context-menu;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}